// Utility functions for PracticeTest component
import { getTranslation as getTranslationHelper } from '../../utils/translationHelpers';
import { FIRST_TEN_IN_ORDER } from '../../services/aiCoach.metadata';
import { logger } from '../../utils/logger';
import { WEAK_THRESHOLD } from '../../config/recommendationConfig';
import { TEST_METADATA } from './PracticeTest.metadata';

export { TEST_METADATA, WEAK_THRESHOLD };

/** CBR 2026 topics in tier order (25 + ADAS bonus = 26). */
export const ALL_TOPICS_IN_ORDER: string[] = [
  // Tier 1: Critical (8)
  'road-markings',
  'motorway-rules',
  'traffic-signs',
  'vehicle-handling',
  'tram-interactions',
  'risk-awareness',
  'night-visibility',
  'adas-safety',
  // Tier 2: Important (9)
  'weather-conditions',
  'incident-first-aid',
  'pedestrian-crossings',
  'vehicle-categories',
  'construction-zones',
  'technology-safety',
  'fatigue-rest',
  'environmental-zones',
  'vehicle-documentation',
  // Tier 3: Enhancement (8)
  'priority-rules',
  'mandatory-signs',
  'traffic-lights-signals',
  'prohibitory-signs',
  'roundabout-rules',
  'emergency-procedures',
  'alcohol-drugs',
  'specialized-driving',
];

/** Foundation topics in order for "Next test" (spec: next incomplete in this order, then weakest). */
export const FOUNDATION_ORDER = ['road-signs', 'priority-rules', 'roundabout-rules', 'hazard-perception', 'speed-safety'];

// Helper function to get test name
export const getTestName = (
  testId: string | undefined,
  t_nested: (key: string) => string,
  getTranslatedTestNameForId: (testId: string) => string
): string => {
  if (!testId) {
    return getTranslationHelper(t_nested, 'practice.title', 'Practice Test');
  }
  
  const translatedName = getTranslatedTestNameForId(testId);
  
  if (translatedName && translatedName.trim() !== '' && !translatedName.startsWith('testNames.')) {
    return translatedName;
  }
  
  const directTranslation = t_nested(`testNames.${testId}`);
  if (directTranslation && directTranslation !== `testNames.${testId}` && !directTranslation.startsWith('testNames.')) {
    return directTranslation;
  }
  
  return TEST_METADATA[testId]?.name || testId || getTranslationHelper(t_nested, 'practice.title', 'Practice Test');
};

// Helper function to calculate motivation word
export const calculateMotivationWord = (
  isCorrect: boolean,
  t_nested: (key: string) => string
): string => {
  if (isCorrect) {
    const words = [
      t_nested('practice.correctFeedback.excellent'),
      t_nested('practice.correctFeedback.perfect'),
      t_nested('practice.correctFeedback.great'),
      t_nested('practice.correctFeedback.correct'),
      t_nested('practice.correctFeedback.wellDone')
    ];
    const selectedWord = words[Math.floor(Math.random() * words.length)];
    return selectedWord && selectedWord.startsWith('practice.') ? 'Excellent!' : selectedWord;
  } else {
    const words = [
      t_nested('practice.incorrectFeedback.tryAgain'),
      t_nested('practice.incorrectFeedback.notQuite'),
      t_nested('practice.incorrectFeedback.incorrect'),
      t_nested('practice.incorrectFeedback.wrongAnswer'),
      t_nested('practice.incorrectFeedback.keepTrying')
    ];
    const selectedWord = words[Math.floor(Math.random() * words.length)];
    return selectedWord && selectedWord.startsWith('practice.') ? 'Try Again' : selectedWord;
  }
};

export const formatTimeLeft = (seconds: number): string => {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remaining = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
};

/** Returns i18n key for motivation word from percentage (spec: aiExam.motivation.*). */
export function getMotivationKey(percent: number | null): string {
  if (percent === null || percent === undefined) return 'aiExam.motivation.none';
  if (percent === 0) return 'aiExam.motivation.start';
  if (percent <= 39) return 'aiExam.motivation.low';
  if (percent <= 59) return 'aiExam.motivation.fair';
  if (percent <= 74) return 'aiExam.motivation.good';
  if (percent <= 84) return 'aiExam.motivation.great';
  if (percent <= 94) return 'aiExam.motivation.excellent';
  return 'aiExam.motivation.ready';
}

export const getOutcomeLabel = (
  isCorrect: boolean,
  questionId: string,
  language: 'en' | 'nl' | 'ar'
): string => {
  const labels = {
    en: {
      correct: ['Correct', 'Well done', 'You got it', 'Nice work', 'That’s right'],
      incorrect: ['Not quite', 'Try again', 'Almost', 'Not yet', 'Incorrect']
    },
    nl: {
      correct: ['Correct', 'Goed gedaan', 'Je hebt het', 'Netjes', 'Dat klopt'],
      incorrect: ['Niet helemaal', 'Probeer opnieuw', 'Bijna', 'Nog niet', 'Onjuist']
    },
    ar: {
      correct: ['صحيح', 'أحسنت', 'أصبت', 'عمل جيد', 'هذا صحيح'],
      incorrect: ['ليس تمامًا', 'حاول مرة أخرى', 'قريب', 'ليس بعد', 'غير صحيح']
    }
  };
  const list = (labels[language] || labels.en)[isCorrect ? 'correct' : 'incorrect'];
  const seed = `${questionId}:${isCorrect ? 'c' : 'i'}:${language}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % list.length;
  return list[index];
};


const tokenize = (text: string): Set<string> => {
  return new Set(
    (text || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, '')
      .split(/\s+/)
      .filter(Boolean)
  );
};

export const findNearestWrongOption = (
  options: Array<{ id: string; text: string }>,
  correctAnswerId: string
): { id: string; text: string } | undefined => {
  const correct = options.find(option => option.id === correctAnswerId);
  const wrongOptions = options.filter(option => option.id !== correctAnswerId);
  if (!correct || wrongOptions.length === 0) return wrongOptions[0];

  const correctTokens = tokenize(correct.text);
  let best = wrongOptions[0];
  let bestScore = -1;

  for (const option of wrongOptions) {
    const tokens = tokenize(option.text);
    let overlap = 0;
    tokens.forEach(token => {
      if (correctTokens.has(token)) overlap += 1;
    });
    if (overlap > bestScore) {
      bestScore = overlap;
      best = option;
    }
  }

  return best;
};

// Helper function to get translated test name for ID
export const createGetTranslatedTestNameForId = (
  currentLanguage: string,
  t_nested: (key: string) => string
): ((testIdToTranslate: string) => string) => {
  return (testIdToTranslate: string): string => {
    if (!testIdToTranslate || testIdToTranslate.trim() === '') {
      return '';
    }
    
    const subjectToTestIdMap: Record<string, string> = {
      'Traffic Lights & Signals': 'traffic-lights-signals',
      'Traffic Lights and Signals': 'traffic-lights-signals',
      'Priority Rules': 'priority-rules',
      'Hazard Perception': 'hazard-perception',
      'Speed & Safety': 'speed-safety',
      'Speed Limits': 'speed-safety',
      'Bicycle Interactions': 'bicycle-interactions',
      'Roundabout Rules': 'roundabout-rules',
      'Tram Interactions': 'tram-interactions',
      'Pedestrian Crossings': 'pedestrian-crossings',
      'Construction Zones': 'construction-zones',
      'Weather Conditions': 'weather-conditions',
      'Road Signs': 'road-signs',
      'Sign Identification': 'sign-identification',
      'Mandatory Signs': 'mandatory-signs',
      'Prohibitory Signs': 'prohibitory-signs',
      'Warning Signs': 'warning-signs',
      'Road Information': 'road-information',
      'Motorway Rules': 'motorway-rules',
      'Vehicle Knowledge': 'vehicle-knowledge',
      'Parking Rules': 'parking-rules',
      'Environmental Zones': 'environmental',
      'Technology & Safety': 'technology-safety',
      'Alcohol & Drugs': 'alcohol-drugs',
      'Fatigue & Rest': 'fatigue-rest',
      'Emergency Procedures': 'emergency-procedures',
      'Insight Practice': 'insight-practice',
    };
    
    const testId = subjectToTestIdMap[testIdToTranslate] || testIdToTranslate;
    const testNameMap: Record<string, string> = {
      'traffic-lights-signals': t_nested('testNames.trafficLightsSignals'),
      'priority-rules': t_nested('testNames.priorityRules'),
      'hazard-perception': t_nested('testNames.hazardPerception'),
      'speed-safety': t_nested('testNames.speedSafety'),
      'bicycle-interactions': t_nested('testNames.bicycleInteractions'),
      'roundabout-rules': t_nested('testNames.roundaboutRules'),
      'tram-interactions': t_nested('testNames.tramInteractions'),
      'pedestrian-crossings': t_nested('testNames.pedestrianCrossings'),
      'construction-zones': t_nested('testNames.constructionZones'),
      'weather-conditions': t_nested('testNames.weatherConditions'),
      'road-signs': t_nested('testNames.roadSigns'),
      'sign-identification': t_nested('testNames.signIdentification'),
      'mandatory-signs': t_nested('testNames.mandatorySigns'),
      'prohibitory-signs': t_nested('testNames.prohibitorySigns'),
      'prohibitory-signs-2': t_nested('testNames.prohibitorySigns2'),
      'warning-signs': t_nested('testNames.warningSigns'),
      'road-information': t_nested('testNames.roadInformation'),
      'motorway-rules': t_nested('testNames.motorwayRules'),
      'vehicle-knowledge': t_nested('testNames.vehicleKnowledge'),
      'parking-rules': t_nested('testNames.parkingRules'),
      'environmental': t_nested('testNames.environmental'),
      'environmental-zones': t_nested('testNames.environmental'),
      'technology-safety': t_nested('testNames.technologySafety'),
      'alcohol-drugs': t_nested('testNames.alcoholDrugs'),
      'fatigue-rest': t_nested('testNames.fatigueRest'),
      'emergency-procedures': t_nested('testNames.emergencyProcedures'),
      'insight-practice': t_nested('testNames.insightPractice'),
      'road-markings': t_nested('testNames.roadMarkings') || 'Road Markings',
      'risk-awareness': t_nested('testNames.riskAwareness') || 'Risk Awareness',
      'night-visibility': t_nested('testNames.nightVisibility') || 'Night & Visibility',
      'incident-first-aid': t_nested('testNames.incidentFirstAid') || 'Incident & First Aid',
      'vehicle-handling': t_nested('testNames.vehicleHandling') || 'Vehicle Handling',
    };
    const translatedName = testNameMap[testId];
    
    if (translatedName && translatedName !== testId && !translatedName.startsWith('testNames.')) {
      return translatedName;
    }
    
    if (testIdToTranslate && testNameMap[testIdToTranslate]) {
      const directTranslation = testNameMap[testIdToTranslate];
      if (directTranslation && !directTranslation.startsWith('testNames.')) {
        return directTranslation;
      }
    }
    
    if (testIdToTranslate === 'Traffic Lights & Signals' || testIdToTranslate === 'Traffic Lights and Signals') {
      const trafficLightsTranslation = t_nested('testNames.trafficLightsSignals');
      if (trafficLightsTranslation && !trafficLightsTranslation.startsWith('testNames.')) {
        return trafficLightsTranslation;
      }
    }
    
    return TEST_METADATA[testId]?.name || testIdToTranslate;
  };
};

// ─── Netflix recommendation engine ───────────────────────────────────────────

export type TopicScore = { topicId: string; accuracy: number };

export type GetNextTestParams = {
  effectiveCompletedCount: number;
  topicScores: TopicScore[];
  completedTopics: string[];
};

/**
 * Returns topic IDs of weakest topics (accuracy < WEAK_THRESHOLD), sorted ascending, limited.
 */
export function getWeakestTopics(
  topicScores: TopicScore[],
  limit: number = 3
): string[] {
  return topicScores
    .filter(t => t.accuracy < WEAK_THRESHOLD)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, limit)
    .map(t => t.topicId);
}

/**
 * Pure recommendation engine. Returns next topic ID.
 * First 10: fixed order. From 11: alternate weak retakes vs new topics. After all 32: pure weak-area mode.
 */
export function getNextTestTopicId(params: GetNextTestParams): string {
  const { effectiveCompletedCount, topicScores, completedTopics } = params;

  // PHASE 1: First 10 fixed order
  if (effectiveCompletedCount < 10) {
    const current = completedTopics[completedTopics.length - 1];
    const currentIndex = FIRST_TEN_IN_ORDER.indexOf(current);
    if (currentIndex !== -1 && currentIndex < FIRST_TEN_IN_ORDER.length - 1) {
      return FIRST_TEN_IN_ORDER[currentIndex + 1];
    }
    return FIRST_TEN_IN_ORDER.find(t => !completedTopics.includes(t)) ?? FIRST_TEN_IN_ORDER[0];
  }

  // PHASE 2: Netflix recommendations from test 11 onward
  const weakTopics = topicScores
    .filter(t => t.accuracy < WEAK_THRESHOLD)
    .sort((a, b) => a.accuracy - b.accuracy)
    .map(t => t.topicId);

  const newTopics = ALL_TOPICS_IN_ORDER.filter(t => !completedTopics.includes(t));

  const isEvenTest = effectiveCompletedCount % 2 === 0;

  if (newTopics.length > 0 && isEvenTest) return newTopics[0];
  if (weakTopics.length > 0 && !isEvenTest) return weakTopics[0];

  if (newTopics.length > 0) return newTopics[0];
  if (weakTopics.length > 0) return weakTopics[0];

  const lowestOverall = [...topicScores].sort((a, b) => a.accuracy - b.accuracy)[0];
  return lowestOverall?.topicId ?? ALL_TOPICS_IN_ORDER[0];
}

// Helper function to get next test
// Uses Netflix-style recommendations when Supabase context provided; else falls back to aiCoach.
export const getNextTest = (
  testId: string | undefined,
  precomputedNextTest: { id: string; name: string } | null,
  getTranslatedTestNameForId: (testId: string) => string,
  t_nested: (key: string) => string,
  netflixStyleRecommendation?: any,
  aiCoach?: any,
  supabaseContext?: GetNextTestParams
): { id: string; name: string } => {
  if (!testId) {
    logger.warn('No testId available for getNextTest');
    return { id: '', name: '' };
  }

  if (supabaseContext) {
    const nextId = getNextTestTopicId(supabaseContext);
    return { id: nextId, name: getTranslatedTestNameForId(nextId) };
  }

  // Fallback: derive from aiCoach (localStorage)
  const testScores = aiCoach?.getTestScores() || {};
  const effectiveScores: Record<string, unknown> = { ...testScores };
  if (testId && !effectiveScores[testId]) {
    effectiveScores[testId] = { average: 0, count: 1, lastScore: 0 };
  }
  const effectiveCompletedCount = Object.keys(effectiveScores).length;
  const effectiveCompletedIds = Object.keys(effectiveScores);
  const topicScores: TopicScore[] = effectiveCompletedIds.map(id => ({
    topicId: id,
    accuracy: (effectiveScores[id] as { average?: number })?.average ?? 0
  }));
  const fallbackParams: GetNextTestParams = {
    effectiveCompletedCount,
    topicScores,
    completedTopics: effectiveCompletedIds
  };
  const nextId = getNextTestTopicId(fallbackParams);
  return { id: nextId, name: getTranslatedTestNameForId(nextId) };
};
